
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useCreateApiEndpoint } from '@/hooks/useApiProviders';
import { useToast } from '@/hooks/use-toast';
import { Loader2, FileUp } from 'lucide-react';
import { importOpenApiEndpoints } from '@/utils/openApiParser';

interface ImportOpenApiSpecProps {
  providerId: string;
}

export function ImportOpenApiSpec({ providerId }: ImportOpenApiSpecProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [importResult, setImportResult] = useState<{ count: number } | null>(null);
  const { mutateAsync: createEndpoint } = useCreateApiEndpoint();
  const { toast } = useToast();
  
  const handleImport24SevenSpec = async () => {
    try {
      setIsLoading(true);
      
      // Fetch the 24Seven OpenAPI specification
      const response = await fetch('https://rest-api.developer.24sevenoffice.com/doc/v1.json');
      
      if (!response.ok) {
        throw new Error(`Failed to fetch API specification: ${response.statusText}`);
      }
      
      const spec = await response.json();
      
      // Import the endpoints
      const count = await importOpenApiEndpoints(spec, providerId, createEndpoint);
      
      setImportResult({ count });
      toast({
        title: "Import successful",
        description: `Imported ${count} endpoints from 24Seven API specification.`,
      });
    } catch (error) {
      console.error("Import error:", error);
      toast({
        variant: "destructive",
        title: "Import failed",
        description: error instanceof Error ? error.message : "Failed to import API specification.",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>24Seven API Specification</CardTitle>
        <CardDescription>
          Import endpoints from the 24Seven OpenAPI specification
        </CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          This will fetch the latest 24Seven API specification and add all endpoints to this provider.
          Existing endpoints will not be modified.
        </p>
        
        {importResult && (
          <div className="bg-muted p-3 rounded-md mb-4">
            <p className="font-medium">Import complete</p>
            <p className="text-sm text-muted-foreground">
              Successfully imported {importResult.count} endpoints.
            </p>
          </div>
        )}
      </CardContent>
      <CardFooter>
        <Button onClick={handleImport24SevenSpec} disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Importing...
            </>
          ) : (
            <>
              <FileUp className="mr-2 h-4 w-4" />
              Import Endpoints
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}
